import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, Download, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { processesUploadsApi } from "api";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface FilePreviewDialogProps {
  attachmentId: number | null;
  filename: string;
  onClose: () => void;
  /** Optional: when provided, shows a local file preview without any API call */
  localFile?: File;
}

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
const PDF_EXTS = ["pdf"];
const VIDEO_EXTS = ["mp4", "webm", "mov", "avi", "mkv"];
const AUDIO_EXTS = ["mp3", "wav", "ogg", "m4a", "flac"];
const TEXT_EXTS = ["txt", "csv", "json", "xml", "md", "yaml", "yml", "log", "ts", "tsx", "js", "jsx", "html", "css"];

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

/**
 * Converts a base64 data-URI (or raw base64) string into a Blob URL.
 */
function base64ToBlobUrl(base64: string, mime: string): string {
  // Backend returns a full data-URI like "data:image/png;base64,..."
  const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
  const byteChars = atob(base64Data);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mime });
  return URL.createObjectURL(blob);
}

function getMimeType(ext: string): string {
  if (IMAGE_EXTS.includes(ext)) {
    if (ext === "svg") return "image/svg+xml";
    if (ext === "gif") return "image/gif";
    if (ext === "webp") return "image/webp";
    if (ext === "bmp") return "image/bmp";
    return `image/${ext === "jpg" ? "jpeg" : ext}`;
  }
  if (ext === "pdf") return "application/pdf";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "mov") return "video/quicktime";
  if (ext === "mp3") return "audio/mpeg";
  if (ext === "wav") return "audio/wav";
  if (ext === "ogg") return "audio/ogg";
  if (ext === "m4a") return "audio/mp4";
  return "application/octet-stream";
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({
  attachmentId,
  filename,
  onClose,
  localFile,
}) => {
  const translations = useTranslations();
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [rawBase64, setRawBase64] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const ext = getExtension(filename);
  const open = attachmentId !== null || localFile !== undefined;

  useEffect(() => {
    // Local file preview: bypass API entirely
    if (localFile) {
      let objectUrl: string | null = null;
      setBlobUrl(null);
      setTextContent(null);
      setRawBase64(null);
      setError(false);

      if (TEXT_EXTS.includes(ext)) {
        const reader = new FileReader();
        reader.onload = (e) => setTextContent(e.target?.result as string ?? null);
        reader.onerror = () => setError(true);
        reader.readAsText(localFile);
      } else if (
        IMAGE_EXTS.includes(ext) ||
        PDF_EXTS.includes(ext) ||
        VIDEO_EXTS.includes(ext) ||
        AUDIO_EXTS.includes(ext)
      ) {
        objectUrl = URL.createObjectURL(localFile);
        setBlobUrl(objectUrl);
      }

      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    // Server attachment preview
    if (!open || attachmentId === null) return;

    let objectUrl: string | null = null;
    setLoading(true);
    setBlobUrl(null);
    setTextContent(null);
    setRawBase64(null);
    setError(false);

    (async () => {
      try {
        const { data } = await processesUploadsApi.get(`/${attachmentId}`);
        const base64: string = data.blob;

        setRawBase64(base64);

        if (TEXT_EXTS.includes(ext)) {
          // Decode text
          try {
            const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
            const decoded = decodeURIComponent(escape(atob(base64Data)));
            setTextContent(decoded);
          } catch {
            setTextContent(atob(base64.includes(",") ? base64.split(",")[1] : base64));
          }
        } else if (
          IMAGE_EXTS.includes(ext) ||
          PDF_EXTS.includes(ext) ||
          VIDEO_EXTS.includes(ext) ||
          AUDIO_EXTS.includes(ext)
        ) {
          const mime = getMimeType(ext);
          objectUrl = base64ToBlobUrl(base64, mime);
          setBlobUrl(objectUrl);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentId, open, localFile]);

  // Cleanup blobUrl on close
  useEffect(() => {
    if (!open && blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  }, [open, blobUrl]);

  const handleDownload = () => {
    if (!rawBase64) return;
    const a = document.createElement("a");
    a.href = rawBase64;
    a.download = filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <Typography color="error">
            {getTranslation(translations, "generic.error") || "Errore nel caricamento del file."}
          </Typography>
        </Box>
      );
    }

    if (IMAGE_EXTS.includes(ext) && blobUrl) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flex={1}
          sx={{ overflow: "auto", p: 1 }}
        >
          <img
            src={blobUrl}
            alt={filename}
            style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (PDF_EXTS.includes(ext) && blobUrl) {
      return (
        <Box flex={1} sx={{ minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", display: "flex", flexDirection: "column" }}>
          <embed
            src={blobUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ flex: 1, minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}
          />
        </Box>
      );
    }

    if (VIDEO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video controls style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </video>
        </Box>
      );
    }

    if (AUDIO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} p={3}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls style={{ width: "100%" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </audio>
        </Box>
      );
    }

    if (TEXT_EXTS.includes(ext) && textContent !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          <pre style={{ margin: 0, fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {textContent}
          </pre>
        </Box>
      );
    }

    // Fallback: no preview available
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        gap={2}
        minHeight={200}
      >
        <Typography color="text.secondary">
          {getTranslation(translations, "attachment.preview.unavailable") || "Anteprima non disponibile per questo tipo di file."}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullscreen}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 2,
          minHeight: isFullscreen ? "100vh" : "60vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Custom title bar */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} flex={1} noWrap title={filename}>
          {filename}
        </Typography>

        {rawBase64 && (
          <Tooltip title={getTranslation(translations, "settings.download.label") || "Download"}>
            <IconButton size="small" onClick={handleDownload}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isFullscreen
          ? (getTranslation(translations, "generic.exitfullscreen") || "Esci dalla schermata intera")
          : (getTranslation(translations, "generic.fullscreen") || "Schermata intera")
        }>
          <IconButton size="small" onClick={() => setIsFullscreen((prev) => !prev)}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={getTranslation(translations, "pm.minimap.close.lable") || "Chiudi"}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
        }}
      >
        {renderPreview()}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
